import React, { useState, useEffect } from "react";
import { showErrorToast, showSuccessToast } from "../../../components/Constant/Notification";
import teamService from "../../../components/Services/TeamService";
import gelTeamService from "../../Services/teamServices";
import shiftTimeService from "../../Services/shiftTimeService";

const ShiftTimeForm = ({ isEditing, editingShift, setData, data, setIsShiftTimeOpen }) => {
  const [shiftLoginTime, setShiftLoginTime] = useState("");
  const [shiftLogoutTime, setShiftLogoutTime] = useState("");
  const [weekendLoginTime, setWeekendLoginTime] = useState("");
  const [weekendLogoutTime, setWeekendLogoutTime] = useState("");
  const [selectedTab, setSelectedTab] = useState("Team");
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedUsersWithTime, setSelectedUsersWithTime] = useState([]);
  const [selectAll, setSelectAll] = useState({});
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [teamMembersLoading, setTeamMembersLoading] = useState({});

  // Get current user data
  const userData = JSON.parse(localStorage.getItem("userData"));
  const currentUserId = userData?.user?.uid;

 
  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // Initialize form with editingShift data
  useEffect(() => {
    if (isEditing && editingShift) {
      setShiftLoginTime(editingShift.weekdayLoginRailwayTime || "");
      setShiftLogoutTime(editingShift.weekdayLogoutRailwayTime || "");
      setWeekendLoginTime(editingShift.weekendLoginRailwayTime || "");
      setWeekendLogoutTime(editingShift.weekendLogoutRailwayTime || "");

      // Initialize selected teams and users
      const fetchTeamData = async () => {
        try {
          const response = await teamService.getTeamName();
          const teamData = response.data?.teams || [];
          const team = editingShift.team?.name !== "No Team"
            ? teamData.find(t => t.name === editingShift.team?.name)
            : null;

          const selectedTeam = team
            ? [{ id: team.teamId, name: team.name, members: [] }]
            : [];
          
          setSelectedTeams(selectedTeam);

          // Initialize selected users
          const usersWithTime = editingShift.members?.map(member => ({
            id: member.id,
            name: member.name,
            teamName: editingShift.team?.name || "Individual",
            loginTime: editingShift.weekdayLoginRailwayTime,
            logoutTime: editingShift.weekdayLogoutRailwayTime,
            weekendLoginTime: editingShift.weekendLoginRailwayTime,
            weekendLogoutTime: editingShift.weekendLogoutRailwayTime
          })) || [];

          setSelectedUsersWithTime(usersWithTime);

          // Fetch team members to check selectAll state
          if (team) {
            const teamResponse = await teamService.getTeamData(team.teamId);
            const teamMembers = [
              ...(Array.isArray(teamResponse.data.BOD) ? teamResponse.data.BOD.map(bod => ({ id: bod.id, name: bod.name })) : []),
              ...(teamResponse.data.MD && teamResponse.data.MD.id ? [{ id: teamResponse.data.MD.id, name: teamResponse.data.MD.name }] : []),
              ...(Array.isArray(teamResponse.data.members) ? teamResponse.data.members.map(member => ({ id: member.id, name: member.name })) : []),
            ];

            // Update selectedTeams with full member list
            setSelectedTeams(prev => prev.map(t => t.id === team.teamId ? { ...t, members: teamMembers } : t));
            setTeams(prev => prev.map(t => t.id === team.teamId ? { ...t, members: teamMembers } : t));

            // Check if all team members are selected
            const filteredTeamMembers = teamMembers.filter(user =>
              user.name.toLowerCase().includes(userSearchQuery.toLowerCase())
            );
            const allSelected = filteredTeamMembers.every(member =>
              usersWithTime.some(u => u.id === member.id)
            );
            setSelectAll({ [team.teamId]: allSelected });
          }
        } catch (error) {
          console.error("Error fetching team data for edit:", error);
        }
      };

      fetchTeamData();
    }
  }, [isEditing, editingShift, userSearchQuery]);

  // Fetch teams on component mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await teamService.getTeamName();
        const data = response.data?.teams || [];
        setTeams(data.map((team) => ({
          id: team.teamId,
          name: team.name,
          members: []
        })));
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };
    fetchTeams();
  }, []);

  // Fetch all users when User tab is selected
  useEffect(() => {
    const fetchUsers = async () => {
      if (selectedTab === "User") {
        setIsLoading(true);
        try {
          const response = await teamService.getUserName();
          const data = response.data.users || [];
          setUsers(data.map((user) => ({ id: user.id, name: user.name })));
        } catch (error) {
          console.error("Error fetching users:", error);
          setUsers([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchUsers();
  }, [selectedTab]);

  // Fetch team members when a team is selected
  const fetchTeamMembers = async (team) => {
    if (team.members.length > 0) return team.members;
    
    setTeamMembersLoading(prev => ({ ...prev, [team.id]: true }));
    try {
      const response = await gelTeamService.getGeoLocationTeamMembers(team.id);
        
      const combinedUsers = response.data.map(member => ({ id: member.value, name: member.label }));
      
      setTeams((prev) =>
        prev.map((t) => (t.id === team.id ? { ...t, members: combinedUsers } : t))
      );
    
      setSelectedTeams((prev) =>
        prev.map((t) => (t.id === team.id ? { ...t, members: combinedUsers } : t))
      );

      return combinedUsers;
    } catch (error) {
      console.error("Error fetching team members:", error);
      return [];
    } finally {
      setTeamMembersLoading(prev => ({ ...prev, [team.id]: false }));
    }
  };

  // Handle team selection (toggle) - Only show members, don't auto-select
  const handleTeamSelect = async (team) => {
    const isTeamSelected = selectedTeams.some((t) => t.id === team.id);

    if (isTeamSelected) {
      // Deselect team and remove all its users
      setSelectedTeams((prev) => prev.filter((t) => t.id !== team.id));
      setSelectedUsersWithTime((prev) => prev.filter((u) => u.teamName !== team.name));
      setSelectAll((prev) => ({ ...prev, [team.id]: false }));
    } else {
      // Select team but DON'T automatically add members - just show them
      const teamWithMembers = { ...team };
      
      // Fetch team members if not already loaded
      const members = await fetchTeamMembers(team);
      teamWithMembers.members = members;
      
      setSelectedTeams((prev) => [...prev, teamWithMembers]);
      
      // Set selectAll to false since no members are selected yet
      setSelectAll((prev) => ({ ...prev, [team.id]: false }));
    }
  };

  // Handle user selection (for individual users or team members)
  const handleUserSelect = (user, teamName = "Individual") => {
    const isSelected = selectedUsersWithTime.some((u) => u.id === user.id);

    if (isSelected) {
      setSelectedUsersWithTime((prev) => prev.filter((u) => u.id !== user.id));
    } else {
      setSelectedUsersWithTime((prev) => [
        ...prev,
        {
          ...user,
          loginTime: shiftLoginTime,
          logoutTime: shiftLogoutTime,
          weekendLoginTime,
          weekendLogoutTime,
          teamName,
        },
      ]);
    }

    // Update selectAll state for the team
    if (teamName !== "Individual") {
      const team = selectedTeams.find((t) => t.name === teamName);
      if (team && team.members.length > 0) {
        const filteredTeamUsers = team.members.filter((u) =>
          u.name.toLowerCase().includes(userSearchQuery.toLowerCase())
        );
        const allSelected = filteredTeamUsers.every((u) =>
          selectedUsersWithTime.some((su) => su.id === u.id) || u.id === user.id
        );
        setSelectAll((prev) => ({ ...prev, [team.id]: allSelected }));
      }
    }
  };

  // Handle select all users for a team or individual users
  const handleSelectAllUsers = (team = null) => {
    const teamId = team ? team.id : "individual";
    const isTeamSelectAll = selectAll[teamId] || false;
    const usersToSelect = team
      ? team.members.filter((user) =>
          user.name.toLowerCase().includes(userSearchQuery.toLowerCase())
        )
      : users.filter((user) =>
          user.name.toLowerCase().includes(userSearchQuery.toLowerCase())
        );

    if (isTeamSelectAll) {
      // Deselect all users from this team
      setSelectedUsersWithTime((prev) =>
        prev.filter((u) => u.teamName !== (team ? team.name : "Individual"))
      );
      setSelectAll((prev) => ({ ...prev, [teamId]: false }));
    } else {
      // Select all users for this team
      const newUsers = usersToSelect.map((user) => ({
        ...user,
        loginTime: shiftLoginTime,
        logoutTime: shiftLogoutTime,
        weekendLoginTime,
        weekendLogoutTime,
        teamName: team ? team.name : "Individual",
      }));

      setSelectedUsersWithTime((prev) => {
        const existingIds = new Set(prev.map((u) => u.id));
        const toAdd = newUsers.filter((u) => !existingIds.has(u.id));
        return [...prev, ...toAdd];
      });
      setSelectAll((prev) => ({ ...prev, [teamId]: true }));
    }
  };

  // Handle remove team from selected
  const handleRemoveTeam = (teamName) => {
    const team = selectedTeams.find((t) => t.name === teamName);
    if (team) {
      setSelectedTeams((prev) => prev.filter((t) => t.name !== teamName));
      setSelectedUsersWithTime((prev) => prev.filter((u) => u.teamName !== teamName));
      setSelectAll((prev) => ({ ...prev, [team.id]: false }));
    }
  };

  // Handle remove individual user
  const handleRemoveUser = (user) => {
    setSelectedUsersWithTime((prev) => prev.filter((u) => u.id !== user.id));
    const team = selectedTeams.find((t) => t.name === user.teamName);
    if (team && team.members.length > 0) {
      const filteredTeamUsers = team.members.filter((u) =>
        u.name.toLowerCase().includes(userSearchQuery.toLowerCase())
      );
      const allSelected = filteredTeamUsers.every((u) =>
        selectedUsersWithTime.some((su) => su.id === u.id && su.id !== user.id)
      );
      setSelectAll((prev) => ({ ...prev, [team.id]: allSelected }));
    } else if (user.teamName === "Individual") {
      // Update individual select all state
      const remainingIndividualUsers = selectedUsersWithTime.filter(u => 
        u.teamName === "Individual" && u.id !== user.id
      );
      const filteredUsers = users.filter((u) =>
        u.name.toLowerCase().includes(userSearchQuery.toLowerCase())
      );
      const allIndividualSelected = filteredUsers.every(u => 
        remainingIndividualUsers.some(ru => ru.id === u.id)
      );
      setSelectAll((prev) => ({ ...prev, individual: allIndividualSelected }));
    }
  };

  // Validate and save shift times
  const handleSaveShift = async () => {
    if (!shiftLoginTime || !shiftLogoutTime || !weekendLoginTime || !weekendLogoutTime) {
      showErrorToast("Please set all login and logout times");
      return;
    }

    if (selectedUsersWithTime.length === 0) {
      showErrorToast("Please select at least one user");
      return;
    }

    const loginMinutes = timeToMinutes(shiftLoginTime);
    const logoutMinutes = timeToMinutes(shiftLogoutTime);
    const weekendLoginMinutes = timeToMinutes(weekendLoginTime);
    const weekendLogoutMinutes = timeToMinutes(weekendLogoutTime);

    if (logoutMinutes <= loginMinutes) {
      showErrorToast("Weekday logout time must be after login time");
      return;
    }

    if (weekendLogoutMinutes <= weekendLoginMinutes) {
      showErrorToast("Weekend logout time must be after login time");
      return;
    }

    const hasIndividuals = selectedUsersWithTime.some((u) => u.teamName === "Individual");
    const teamId = selectedTeams.length === 1 && !hasIndividuals ? selectedTeams[0].id : null;

    try {
      // Create proper payload structure
      let payload = {
        weekDayLoginTime: shiftLoginTime,
        weekDayLogoutTime: shiftLogoutTime,
        weekEndLoginTime: weekendLoginTime,
        weekEndLogoutTime: weekendLogoutTime,
        userIds: selectedUsersWithTime.map((user) => user.id),
        teamIds: selectedTeams.map((team) => team.id).filter(id => id),
        createdById: currentUserId,
        modifiedById: currentUserId,
      };

      let response;

      if (isEditing && editingShift) {
        payload = {
          ...payload,
          shiftId: editingShift.id,
        };
        console.log("🟡 Update Payload:", payload);
        response = await shiftTimeService.updateShiftTime(payload);
      } else {
        console.log("🟢 Create Payload:", payload);
        response = await shiftTimeService.createShiftTime(payload);
      }

      if (response.status === 200 || response.status === 201) {
        // Create consistent data structure for the table
        const newShiftEntry = {
          id: isEditing ? editingShift.id : response.data.id,
          weekdayLoginRailwayTime: shiftLoginTime,
          weekdayLogoutRailwayTime: shiftLogoutTime,
          weekendLoginRailwayTime: weekendLoginTime,
          weekendLogoutRailwayTime: weekendLogoutTime,
          weekdayShiftTime: `${convertTo12Hour(shiftLoginTime)} - ${convertTo12Hour(shiftLogoutTime)}`,
          weekendShiftTime: `${convertTo12Hour(weekendLoginTime)} - ${convertTo12Hour(weekendLogoutTime)}`,
          membersCount: selectedUsersWithTime.length,
          teams: selectedTeams.map(team => ({ id: team.id, name: team.name })),
          members: selectedUsersWithTime.map(user => ({
            id: user.id,
            name: user.name,
            teamName: user.teamName
          })),
          team: teamId ? { name: selectedTeams[0].name, id: teamId } : { name: "No Team" },
          createdById: currentUserId,
          modifiedById: currentUserId,
        };

        if (isEditing) {
          setData(data.map((item) => (item.id === editingShift.id ? newShiftEntry : item)));
        } else {
          setData([...data, newShiftEntry]);
        }

        showSuccessToast(
          `Shift times ${isEditing ? "updated" : "created"} successfully for ${
            selectedUsersWithTime.length
          } users`
        );
        setIsShiftTimeOpen(false);
      }
    } catch (error) {
      console.error("❌ Error saving shift times:", error);
      showErrorToast(error.response?.data?.message || "Failed to save shift times. Please try again.");
    }
  };

  // Helper function to convert time to 12-hour format
  const convertTo12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const filteredTeams = teams.filter((team) =>
    team.name.toLowerCase().includes(teamSearchQuery.toLowerCase())
  );

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  // Group selected users by teamName for display
  const groupedSelected = selectedUsersWithTime.reduce((acc, user) => {
    if (!acc[user.teamName]) {
      acc[user.teamName] = [];
    }
    acc[user.teamName].push(user);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-[600px] max-h-[90vh]  overflow-hidden">
     

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto ">
        {/* Time Input Section - Enhanced */}
        <div className="mb-8 bg-gray-50 rounded-xl p-5 border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-customGreen rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Working Hours</h3>
          </div>
          
          <div className="space-y-5">
            {/* Weekdays */}
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Weekdays (Monday - Friday)</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Login Time</label>
                  <input
                    type="time"
                    value={shiftLoginTime}
                    onChange={(e) => setShiftLoginTime(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-customGreen focus:border-customGreen transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Logout Time</label>
                  <input
                    type="time"
                    value={shiftLogoutTime}
                    onChange={(e) => setShiftLogoutTime(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-customGreen focus:border-customGreen transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Weekends */}
            <div className="bg-white rounded-lg p-4 ">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Weekends (Saturday - Sunday)</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Login Time</label>
                  <input
                    type="time"
                    value={weekendLoginTime}
                    onChange={(e) => setWeekendLoginTime(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-customGreen focus:border-customGreen transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Logout Time</label>
                  <input
                    type="time"
                    value={weekendLogoutTime}
                    onChange={(e) => setWeekendLogoutTime(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-customGreen focus:border-customGreen transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Selection Section - Enhanced */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-customGreen rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Select Team Members</h3>
          </div>
          
          <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {/* Enhanced Tab Navigation */}
            <div className="flex bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <button
                className={`flex-1 p-4 font-medium text-sm md:text-base transition-all duration-200 relative ${
                  selectedTab === "Team"
                    ? "bg-white text-customGreen shadow-sm"
                    : "text-gray-600 hover:text-gray-800 hover:bg-white hover:bg-opacity-50"
                }`}
                onClick={() => setSelectedTab("Team")}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  By Team
                </div>
                {selectedTab === "Team" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-customGreen"></div>
                )}
              </button>
              <button
                className={`flex-1 p-4 font-medium text-sm md:text-base transition-all duration-200 relative ${
                  selectedTab === "User"
                    ? "bg-white text-customGreen shadow-sm"
                    : "text-gray-600 hover:text-gray-800 hover:bg-white hover:bg-opacity-50"
                }`}
                onClick={() => setSelectedTab("User")}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Individual Users
                </div>
                {selectedTab === "User" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-customGreen"></div>
                )}
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-4 bg-white max-h-72 overflow-y-auto">
              {selectedTab === "Team" && (
                <div className="space-y-4">
                  {/* Enhanced Search Input */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search teams by name..."
                      value={teamSearchQuery}
                      onChange={(e) => setTeamSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-customGreen focus:border-customGreen transition-all"
                    />
                    <svg
                      className="absolute left-3 top-3.5 h-4 w-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>

                  {/* Enhanced Team List */}
                  <div className="space-y-3">
                    {filteredTeams.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <p className="font-medium">No teams found</p>
                        <p className="text-sm text-gray-400 mt-1">Try adjusting your search terms</p>
                      </div>
                    ) : (
                      filteredTeams.map((team) => {
                        const isTeamSelected = selectedTeams.some((t) => t.id === team.id);
                        const selectedTeam = selectedTeams.find((t) => t.id === team.id);
                        const members = selectedTeam?.members || team.members || [];
                        const isLoadingMembers = teamMembersLoading[team.id];
                        
                        return (
                          <div key={team.id} className={`border-2 rounded-xl transition-all duration-200 ${
                            isTeamSelected 
                              ? 'border-customGreen bg-green-50 shadow-md' 
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                          }`}>
                            <label className="flex items-center gap-4 p-4 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isTeamSelected}
                                onChange={() => handleTeamSelect(team)}
                                className="h-5 w-5 text-customGreen focus:ring-customGreen border-gray-300 rounded transition-all"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-gray-800 flex items-center gap-2">
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                  {team.name}
                                </div>
                                <div className="text-sm text-gray-500 flex items-center gap-1">
                                  {isLoadingMembers ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-customGreen"></div>
                                      Loading members...
                                    </>
                                  ) : (
                                    <>
                                      
                                  
                                    </>
                                  )}
                                </div>
                              </div>
                              {isTeamSelected && (
                                <div className="flex items-center gap-1 bg-customGreen text-white px-3 py-1 rounded-full text-xs font-medium">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Expanded
                                </div>
                              )}
                            </label>
                            
                            {/* Members section - only show when team is selected */}
                            {isTeamSelected && members.length > 0 && (
                              <div className="px-4 pb-4 border-t border-green-200 bg-green-25">
                                <div className="mt-3 mb-3">
                                  <label className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg cursor-pointer border-2 border-blue-200 transition-colors hover:bg-blue-100">
                                    <input
                                      type="checkbox"
                                      checked={selectAll[team.id] || false}
                                      onChange={() => handleSelectAllUsers(selectedTeam)}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="font-medium text-blue-700 text-sm">
                                      Select All Members ({members.filter((user) =>
                                        user.name.toLowerCase().includes(userSearchQuery.toLowerCase())
                                      ).length})
                                    </span>
                                  </label>
                                </div>
                                <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                                  {members
                                    .filter((user) =>
                                      user.name.toLowerCase().includes(userSearchQuery.toLowerCase())
                                    )
                                    .map((user) => (
                                      <label
                                        key={user.id}
                                        className="flex items-center gap-3 p-3 hover:bg-white rounded-lg cursor-pointer transition-colors border border-transparent hover:border-gray-200"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selectedUsersWithTime.some((u) => u.id === user.id)}
                                          onChange={() => handleUserSelect(user, team.name)}
                                          className="h-4 w-4 text-customGreen focus:ring-customGreen border-gray-300 rounded"
                                        />
                                        <div className="w-8 h-8 bg-gradient-to-br from-customGreen to-green-600 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                                          {user.name.charAt(0).toUpperCase()}
                                        </div>
                                        <span className="font-medium text-sm truncate flex-1">{user.name}</span>
                                        {selectedUsersWithTime.some((u) => u.id === user.id) && (
                                          <div className="text-customGreen">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                          </div>
                                        )}
                                      </label>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {/* ...existing User tab content... */}
              {selectedTab === "User" && (
                <div className="space-y-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search individual users..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-customGreen focus:border-customGreen transition-all"
                    />
                    <svg
                      className="absolute left-3 top-3.5 h-4 w-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <label className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg cursor-pointer border-2 border-blue-200 transition-colors hover:bg-blue-100">
                    <input
                      type="checkbox"
                      checked={selectAll.individual || false}
                      onChange={() => handleSelectAllUsers()}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="font-medium text-blue-700 text-sm">
                      Select All Users ({filteredUsers.length})
                    </span>
                  </label>
                  <div className="space-y-2">
                    {isLoading ? (
                      <div className="text-center py-8 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-customGreen mx-auto mb-2"></div>
                        <p>Loading users...</p>
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <p>No users found</p>
                      </div>
                    ) : (
                      filteredUsers.map((user) => (
                        <label
                          key={user.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-gray-200"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUsersWithTime.some((u) => u.id === user.id)}
                            onChange={() => handleUserSelect(user)}
                            className="h-4 w-4 text-customGreen focus:ring-customGreen border-gray-300 rounded"
                          />
                          <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-sm truncate flex-1">{user.name}</span>
                          {selectedUsersWithTime.some((u) => u.id === user.id) && (
                            <div className="text-customGreen">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Selected Users Section */}
          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-customGreen rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Selected Users ({selectedUsersWithTime.length})</h3>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-xl p-4 max-h-48 overflow-y-auto">
              {Object.keys(groupedSelected).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                  </svg>
                  <p className="font-medium">No users selected yet</p>
                  <p className="text-sm text-gray-400 mt-1">Select teams or individual users above to get started</p>
                </div>
              ) : (
                Object.keys(groupedSelected).map((teamName) => (
                  <div key={teamName} className="mb-4 p-3 bg-gray-50 rounded-lg border">
                    <div className="flex justify-between items-center font-medium text-gray-700 mb-2">
                      <span className="flex items-center gap-2">
                        {teamName === "Individual" ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        )}
                        {teamName === "Individual" ? "Individual Users" : `Team: ${teamName}`} ({groupedSelected[teamName].length})
                      </span>
                    
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {groupedSelected[teamName].map((user) => (
                        <div key={user.id} className="flex justify-between items-center text-sm bg-white p-2 rounded border">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-customGreen rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="truncate">{user.name}</span>
                          </div>
                          <button
                            onClick={() => handleRemoveUser(user)}
                            className="text-red-500 hover:text-red-700 text-xs ml-2 flex-shrink-0"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
        <button
          onClick={() => setIsShiftTimeOpen(false)}
          className="w-full sm:w-auto bg-gray-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-600 transition-colors text-sm order-2 sm:order-1"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveShift}
          className="w-full sm:w-auto bg-customGreen text-white px-6 py-3 rounded-lg font-medium hover:bg-green-600 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2 flex items-center justify-center gap-2"
          disabled={selectedUsersWithTime.length === 0}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {isEditing ? "Update Shift" : "Create Shift"} ({selectedUsersWithTime.length} users)
        </button>
      </div>
    </div>
  );
};

export default ShiftTimeForm;